import { useEffect, useRef, useState } from 'react';
import { addYears } from 'date-fns';

import type { Payment, PaymentSortField, PaymentSortState } from '@models/payments';
import { PaymentSearchByFields } from '@models/payments';
import { UserPermissions } from '@models/user';
import type { GetPaymentsParams } from '@features/payments/api';
import { usePaymentsQuery } from '@features/payments/queries';
import { useMeStore } from '@features/users/store';
import { useToast } from '@hooks/useToast';
import { userHasPermission } from '@utils/access';
import { formatYmd } from '@utils/date';

import Pagination from '@components/Pagination';
import PaymentsTable from '@components/Payments/PaymentsTable';
import PaymentDetailDrawer from '@components/Payments/PaymentDetailDrawer';
import s from './CustomerDetailPanel.module.css';

const DEFAULT_ROWS_PER_PAGE = 10;
const DEFAULT_SORT: PaymentSortState = { field: 'date', dir: 'desc' };
const PAYMENTS_FROM_DATE = '2023-01-01';

type CustomerPaymentsTabProps = {
  customerId: string;
};

function CustomerPaymentsTab({ customerId }: CustomerPaymentsTabProps) {
  const [sort, setSort] = useState<PaymentSortState>(DEFAULT_SORT);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [selected, setSelected] = useState<Payment | null>(null);

  const queryParams: GetPaymentsParams = {
    page: page + 1,
    limit: rowsPerPage,
    fromDate: PAYMENTS_FROM_DATE,
    toDate: formatYmd(addYears(new Date(), 1)),
    searchQuery: customerId,
    searchBy: PaymentSearchByFields.CUSTOMER_ID,
    sortBy: sort.field,
    sortOrder: sort.dir,
  };

  const { data, isLoading, isFetching, isError } = usePaymentsQuery(queryParams);
  const toast = useToast();
  const me = useMeStore();

  // Read-only users can browse the list but not open the payment detail overlay.
  const canViewPaymentDetails = !!me && userHasPermission(me, UserPermissions.EDIT_USERS);

  // The drawer renders the row it was opened with, so re-read it from the refetched page — otherwise
  // an action taken inside the drawer (capture) leaves it showing the pre-action snapshot.
  const selectedPayment = selected
    ? ((data?.data ?? []).find((row) => row.id === selected.id) ?? selected)
    : null;

  const errorShownRef = useRef(false);
  useEffect(() => {
    if (isError && !errorShownRef.current) {
      toast.error('Failed to load customer payments');
      errorShownRef.current = true;
    }
    if (!isError) errorShownRef.current = false;
  }, [isError, toast]);

  const handleSort = (field: PaymentSortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: 'desc' },
    );
    setPage(0);
  };

  return (
    <div className={s.paymentsTab}>
      <PaymentsTable
        showMethodColumn
        hidePaymentIdColumn
        isLoading={isLoading}
        isFetching={isFetching}
        rows={data?.data ?? []}
        sort={sort}
        onSort={handleSort}
        onRowClick={canViewPaymentDetails ? setSelected : undefined}
      />
      {!isLoading && (
        <Pagination
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          total={data?.meta.itemCount ?? 0}
        />
      )}

      {canViewPaymentDetails && (
        <PaymentDetailDrawer payment={selectedPayment} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

export default CustomerPaymentsTab;
